import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { endpoints, ID } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { api } from '../../lib/apiClient';
import { throwError, throwInfo } from '../../redux/modules/messages/actions';
import { QUERY_KEY_LAZY_QUEUES } from './useLazyQueues';
import { QUERY_KEY_QUEUES_UNPAGINATED } from './useUnpaginatedQueues';

type RenameQueuePayload = { queueId: ID; newName: string };

export const useRenameQueue = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { mutate, ...mutation } = useMutation({
    mutationFn: (payload: RenameQueuePayload) =>
      api.request(
        endpoints.queues.patch(payload.queueId, {
          name: payload.newName,
        })
      ),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_QUEUES_UNPAGINATED],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_LAZY_QUEUES],
      });
      dispatch(throwInfo('queueUpdated'));
    },

    onError: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_QUEUES_UNPAGINATED],
      });
      dispatch(throwError('queueError'));
    },
  });

  const renameQueue = useCallback(
    (payload: RenameQueuePayload) => {
      queryClient.setQueriesData<Queue[] | undefined>(
        [QUERY_KEY_QUEUES_UNPAGINATED],
        prev =>
          prev?.map(q =>
            q.id === payload.queueId ? { ...q, name: payload.newName } : q
          )
      );
      mutate(payload);
    },
    [mutate, queryClient]
  );

  return { ...mutation, mutate: renameQueue };
};
